import { Button, Dropdown } from "components/ui";
import { PLACEMENT } from "components/ui/utils/constant";
import { useEffect, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { HiOutlineCheck } from "react-icons/hi2";
import { MdPlaylistAdd, MdPlaylistPlay } from "react-icons/md";
import InputModern from "components/ui/Input/InputModern";
import { useYoutube } from "utils/hooks/socials/useYoutube";
import { TbPlaylist, TbPlaylistAdd, TbSearch } from "react-icons/tb";

const PlaylistDropdown = ({
    post,
    isEditable = true,
    onChange = (playlist_id) => void 0,
    portal = false
}) => {
    const [ isLoading, setIsLoading ] = useState(false);
    const [ playlists, setPlaylists ] = useState([]);
    const [ newPlaylistName, setNewPlaylistName ] = useState("");
    const [ showCreatePlaylist, setShowCreatePlaylist ] = useState(false);
    const [ isCreatingPlaylist, setIsCreatingPlaylist ] = useState(false);
    const [ searchQuery, setSearchQuery ] = useState('');
    const { getYoutubePlaylists, createYoutubePlaylist } = useYoutube();
    const searchRef = useRef();

    const playlist = playlists?.find(e => e.id == post?.preferences?.playlist_id);

    const handleOnCreatePlaylist = () => {
        setShowCreatePlaylist(true);
    }

    const resetState = () => {
        setNewPlaylistName("");
        setShowCreatePlaylist(false);
        setSearchQuery('');
    }

    const onSearch = (value) => {
        setSearchQuery(value?.toLowerCase()?.trim());
    }

    const filteredPlaylists = playlists?.filter(playlist => {
        if (!searchQuery) return true;
        return playlist.title?.toLowerCase().includes(searchQuery);
    });

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && filteredPlaylists?.length > 0) {
            onChange(filteredPlaylists[0]?.id);
            setSearchQuery('');
        }
    };

    const createPlaylist = async () => {
        if (!newPlaylistName.trim()) return;
        
        setIsCreatingPlaylist(true);
        try {
            const newPlaylist = await createYoutubePlaylist(newPlaylistName);
            setPlaylists([newPlaylist, ...playlists]);
        } catch (error) {
            console.error("Error creating playlist:", error);
        } finally {
            setIsCreatingPlaylist(false);
            resetState();
        }
    }
    
    const getPlaylists = async () => {
        setIsLoading(true);
        try {
            const _playlists = await getYoutubePlaylists();
            setPlaylists(_playlists);
        } catch (error) {
            console.error("Error fetching playlists:", error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getPlaylists();
    }, []);

    return (
        <Dropdown
            placement={PLACEMENT.BOTTOM_START}
            parentClassName="w-full"
            menuClass={`${portal ? "" : "w-full"} max-w-[300px] max-h-[300px] overflow-y-auto`}
            portal={portal}
            disabled={isLoading || !isEditable}
            onClick={resetState}
            onKeyDown={handleKeyDown}
            renderTitle={
                <Button
                    shape="rounded"
                    size={"sm"}
                    className="!rounded-none w-full !p-4 !h-auto border-b border-gray-100 dark:border-gray-700"
                    loading={isLoading}
                    disabled={isLoading || !isEditable}
                >
                    {isLoading 
                        ? <>Loading Playlists...</>
                        : !!playlist
                            ? <div className='flex items-center gap-2'>
                                {playlist?.snippet?.thumbnails?.default?.url 
                                    ? <img src={playlist?.snippet?.thumbnails?.default?.url || playlist?.thumbnail} className="h-6 w-6 object-cover shrink-0" />
                                    : <TbPlaylistAdd className='text-primary-600 text-2xl shrink-0' />
                                }
                                <span>{playlist?.snippet?.title || playlist?.title}</span>
                            </div>
                            :  <div className='flex items-center gap-2'>
                                <TbPlaylistAdd className='text-2xl shrink-0' />
                                <span><Trans i18nKey={`postModalPinterest.selectPlaylist`}>Select Playlist</Trans></span>
                            </div>
                    }
                </Button>
            }
        >
            {!!showCreatePlaylist
                ? <div className="px-2 w-full flex items-center gap-2">
                    <InputModern 
                        autoFocus
                        placeholder="New playlist name..."
                        fieldsetClassName="w-full w-[300px] !bg-transparent flex-1"
                        className="rounded-2xl !bg-transparent w-full w-[300px]"
                        onChange={(e) => setNewPlaylistName(e.target.value)}
                        onKeyDown={(e) => { if (e.key == 'Enter') createPlaylist() }}
                        disabled={isCreatingPlaylist}
                        value={newPlaylistName}
                    />
                    <Button 
                        variant="twoTone"
                        size="sm"
                        className="rounded-full shrink-0"
                        loading={isCreatingPlaylist}
                        onClick={createPlaylist}
                        icon={<HiOutlineCheck />}
                    />
                </div>
                : <>
                    <Dropdown.Item className="!p-0">
                        <div onClick={(e) => { handleOnCreatePlaylist(); e.stopPropagation(); }} className="w-full h-ful flex items-center gap-2 cursor-pointer py-2 px-4">
                            <MdPlaylistAdd className='h-6 md:text-2xl text-violet-600' />
                            <span>Create new playlist...</span>
                        </div>
                    </Dropdown.Item>
                    <Dropdown.Item variant="divider" />
                    <Dropdown.Item variant="header" className="!p-2">
                        <div className="flex items-center gap-2 w-full">
                            <div className="relative flex-1">
                                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-400">
                                    <TbSearch className="text-lg" />
                                </div>
                                <input
                                    value={searchQuery}
                                    autoFocus
                                    ref={searchRef}
                                    className="w-full h-9 pl-9 pr-4 rounded-lg text-sm border border-tertiary-100 dark:border-gray-600 bg-transparent focus:outline-none focus:ring-1 focus:ring-primary-500 dark:text-gray-100"
                                    placeholder="Search..."
                                    onChange={(e) => onSearch(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && onChange(filteredPlaylists?.[0]?.id)}
                                />
                            </div>
                        </div>
                    </Dropdown.Item>
                    {filteredPlaylists?.map((playlist, key) => (
                        <Dropdown.Item 
                            key={key} 
                            onClick={() => onChange(playlist?.id)} 
                            className={`flex items-center gap-2 py-2 px-4 ${post?.preferences?.playlist_id == playlist?.id && `bg-primary-50 dark:bg-primary-900`}`}
                        >
                            {playlist?.thumbnail 
                                ? <img src={playlist.thumbnail} className="w-6 h-6 object-cover rounded shrink-0" />
                                : <MdPlaylistPlay className='text-2xl text-primary-600 shrink-0' />
                            }
                            <span className="text-ellipsis block">{playlist.title}</span>
                        </Dropdown.Item>
                    ))}
                </>
            }
        </Dropdown>
    )
}

export default PlaylistDropdown;